import { styled } from '@mui/material/styles'
import type { SxProps, Theme } from '@mui/material'
import Box from '@/components/common/Box'
import {
	colors,
	typography,
	spacing,
	radius,
	shadows,
	sizing,
	focusVisible,
} from '@/styles/designTokens'

export const PillRoot = styled('button', {
	shouldForwardProp: (prop) =>
		prop !== 'isInteractive' && prop !== 'fullWidth' && prop !== 'compact',
})<{ isInteractive?: boolean; fullWidth?: boolean; compact?: boolean }>(
	({ isInteractive, fullWidth, compact }) => ({
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		gap: spacing.sm,
		height: compact ? undefined : sizing.buttonHeight,
		padding: compact ? 0 : `${spacing.sm}px ${spacing.lg}px`,
		borderRadius: compact ? 0 : radius.pill,
		border: 'none',
		outline: compact ? 'none' : `1px solid ${colors.border}`,
		outlineOffset: 0,
		backgroundColor: compact ? 'transparent' : colors.surfaceWhite,
		color: colors.textPrimary,
		fontFamily: typography.fontFamily,
		flexShrink: 0,
		cursor: isInteractive ? 'pointer' : 'default',
		width: fullWidth ? '100%' : undefined,
		maxWidth: fullWidth ? '100%' : sizing.schedulePillMaxWidth,
		'&:focus-visible': {
			outline: `${focusVisible.outline} ${colors.overlayHeavy}`,
			outlineOffset: focusVisible.outlineOffset,
		},
	}),
)

export const PillLabel = styled('span')({
	fontSize: typography.fontSizeMd,
	lineHeight: typography.lineHeightMd,
	color: colors.textPrimary,
	display: 'inline-flex',
	alignItems: 'baseline',
	gap: spacing.xs,
	minWidth: 0,
	overflow: 'hidden',
})

export const PillLabelBold = styled('span')({
	fontWeight: typography.fontWeightBold,
	minWidth: 0,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
})

export const PillLabelNormal = styled('span')({
	fontWeight: typography.fontWeightRegular,
	flexShrink: 0,
	whiteSpace: 'nowrap',
})

export const PillIconWrap = styled(Box)({
	marginLeft: 'auto',
	display: 'inline-flex',
	flexShrink: 0,
})

export const dropdownItemSx: SxProps<Theme> = {
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeMd}px`,
	paddingTop: `${spacing.md}px`,
	paddingBottom: `${spacing.md}px`,
	paddingLeft: `${spacing.xl}px`,
	paddingRight: `${spacing.xl}px`,
}

// STO-3834: off-hours variant for dine-in. Ember uses a `greyed-out-text` class on the option
// label (category-tabs.hbs:42) — the entry stays listed and selectable, only muted.
export const dropdownItemOffHoursSx: SxProps<Theme> = {
	...dropdownItemSx,
	color: colors.textSecondary,
}

export const dropdownPaperSx: SxProps<Theme> = {
	mt: 0.5,
	borderRadius: `${radius.dropdown}px`,
	boxShadow: shadows.dropdown,
	minWidth: sizing.moreMenuMinWidth,
}
